/**
 * Notes' writes, made offline (unit 25C2). Each shows at once, from the
 * cache, and queues the request that makes it true.
 *
 * **An edit to a note's words says which version it was made from**
 * (`basedOn`, sent as `X-Based-On`): if the note changed on another device in
 * the meantime, the server refuses it, and the outbox keeps it as a failure
 * with its text — never lost, never silently written over the other (v1.91).
 * A move, a tag or a drag carries no base: there the last write wins (D4).
 *
 * The editor saves as the person types (v1.8); each save is a `PATCH`, and
 * the outbox merges an unsent one into the next, so a long afternoon offline
 * is one queued write.
 *
 * Links (`linked_note_ids`) are the server's to derive from `[[wikilinks]]`;
 * a note's cached links are the server's last word until its row comes down.
 */

import type { Schemas } from "../api/client";
import { edit, row } from "./cache";
import { enqueue } from "./outbox";
import { uuid7 } from "./uuid7";

type Note = Schemas["SyncedNote"];
type Notebook = Schemas["Notebook"];
type NoteIn = Partial<Omit<Schemas["NoteIn"], "id">>;
type NotePatch = Schemas["NotePatch"];

export async function createNote(
  db: IDBDatabase,
  input: NoteIn,
  now: Date = new Date(),
): Promise<Note> {
  const id = uuid7(now.getTime());
  const stamp = now.toISOString();
  const note: Note = {
    id,
    title: (input.title ?? "").trim(),
    body: input.body ?? "",
    notebook_id: input.notebook_id ?? null,
    tags: input.tags ?? [],
    memory_ids: input.memory_ids ?? [],
    goal_ids: input.goal_ids ?? [],
    reading_ids: input.reading_ids ?? [],
    linked_note_ids: [],
    source: input.source ?? "app",
    position: null,
    created_at: stamp,
    updated_at: stamp,
    weave: null,
  };
  await enqueue(
    db,
    { method: "POST", path: "/api/notes", body: { ...input, id }, entity: "note", entityId: id },
    { now, effect: (cache) => cache.put({ entity: "note", id, row: note }) },
  );
  return note;
}

/** The words of a note are what two devices can lose to each other. */
function touchesText(patch: NotePatch): boolean {
  return patch.title != null || patch.body != null;
}

/**
 * `base` overrides the version read from the cache: a conflict kept by the
 * person is made from the version they were shown (27B, `Conflict.tsx`).
 */
export async function updateNote(
  db: IDBDatabase,
  id: string,
  patch: NotePatch,
  now: Date = new Date(),
  base?: string,
): Promise<void> {
  const basedOn = touchesText(patch)
    ? (base ?? (await row(db, "note", id))?.updated_at)
    : undefined;
  const { archived, ...fields } = patch;
  const shown = Object.fromEntries(Object.entries(fields).filter(([, value]) => value != null));
  return enqueue(
    db,
    {
      method: "PATCH",
      path: `/api/notes/${id}`,
      body: patch,
      entity: "note",
      entityId: id,
      ...(basedOn === undefined ? {} : { basedOn }),
    },
    {
      now,
      // `updated_at` is left as the server's: it is the base the next edit reads.
      effect: (cache) =>
        edit(cache, "note", id, (note) => ({
          ...note,
          ...shown,
          ...("notebook_id" in patch ? { notebook_id: patch.notebook_id ?? null } : {}),
          // v2.01: the server keeps the first time it was archived, as here.
          ...(archived == null
            ? {}
            : { archived_at: archived ? (note.archived_at ?? now.toISOString()) : null }),
          id,
        })),
    },
  );
}

export function deleteNote(db: IDBDatabase, id: string, now: Date = new Date()): Promise<void> {
  return enqueue(
    db,
    { method: "DELETE", path: `/api/notes/${id}`, entity: "note", entityId: id },
    { now, effect: (cache) => cache.delete(["note", id]) },
  );
}

/** Where a dot was dropped on the Weave (v1.72): a state, so a replay puts it there again. */
export function placeNote(
  db: IDBDatabase,
  id: string,
  x: number,
  y: number,
  now: Date = new Date(),
): Promise<void> {
  return enqueue(
    db,
    {
      method: "PUT",
      path: `/api/notes/weave/positions/${id}`,
      body: { x, y },
      entity: "note",
      entityId: id,
    },
    { now, effect: (cache) => edit(cache, "note", id, (note) => ({ ...note, weave: { x, y } })) },
  );
}

export async function createNotebook(
  db: IDBDatabase,
  name: string,
  color: string | null = null,
  now: Date = new Date(),
): Promise<Notebook> {
  const id = uuid7(now.getTime());
  const stamp = now.toISOString();
  const notebook: Notebook = {
    id,
    name: name.trim(),
    description: "",
    color,
    note_count: 0,
    created_at: stamp,
    updated_at: stamp,
  };
  await enqueue(
    db,
    {
      method: "POST",
      path: "/api/notes/notebooks",
      body: { id, name, color },
      entity: "notebook",
      entityId: id,
    },
    { now, effect: (cache) => cache.put({ entity: "notebook", id, row: notebook }) },
  );
  return notebook;
}

export function updateNotebook(
  db: IDBDatabase,
  id: string,
  patch: Schemas["NotebookPatch"],
  now: Date = new Date(),
): Promise<void> {
  const shown = Object.fromEntries(Object.entries(patch).filter(([, value]) => value != null));
  return enqueue(
    db,
    {
      method: "PATCH",
      path: `/api/notes/notebooks/${id}`,
      body: patch,
      entity: "notebook",
      entityId: id,
    },
    { now, effect: (cache) => edit(cache, "notebook", id, (book) => ({ ...book, ...shown, id })) },
  );
}

/** Its notes fall back to Misc (notes rule 10), in the cache as on the server. */
export function deleteNotebook(db: IDBDatabase, id: string, now: Date = new Date()): Promise<void> {
  return enqueue(
    db,
    { method: "DELETE", path: `/api/notes/notebooks/${id}`, entity: "notebook", entityId: id },
    {
      now,
      effect: (cache) => {
        cache.delete(["notebook", id]);
        const walk = cache.openCursor();
        walk.onsuccess = () => {
          const at = walk.result;
          if (!at) return;
          const cached = at.value as { entity: string; row: Note };
          if (cached.entity === "note" && cached.row.notebook_id === id)
            at.update({ ...cached, row: { ...cached.row, notebook_id: null } });
          at.continue();
        };
      },
    },
  );
}
